import { createContext, useContext, useCallback } from 'react';
import throttle from 'lodash/throttle';
import { pushEvent } from '../../client/data-layer';

export const ProductCollectionTrackingContext = createContext({
  categoryId: null as string | null | undefined,
  categoryName: null as string | null | undefined,
});

type FilterContexts =
  | 'facet-navigation'
  | 'quick-filter'
  | 'sidebar-filters'
  | 'mobile-overlay'
  | 'product-name-search';
export const ProductCollectionFilterContext = createContext({
  filterLayout: null as null | FilterContexts,
});

type FilterEventProps = {
  type:
    | 'list'
    | 'list-tree'
    | 'boolean'
    | 'range'
    | 'range-groups'
    | 'product-name-search'
    | 'facet-navigation';
  property: string;
  title: string;
};
export const useTrackFilterEvent = ({
  type,
  property,
  title,
}: FilterEventProps) => {
  const { categoryId, categoryName } = useContext(
    ProductCollectionTrackingContext
  );
  const { filterLayout } = useContext(ProductCollectionFilterContext);
  const trackFilterEvent = useCallback(
    (value: string | null = null) => {
      pushEvent('productCollectionFilterUpdate', {
        type,
        title,
        value,
        property,
        categoryId,
        categoryName,
        filterLayout,
      });
    },
    [filterLayout, categoryId, categoryName, type, property, title]
  );

  const trackFilterEventWithThrottle = useCallback(
    // Only send an event evert 4s.
    throttle(trackFilterEvent, 4000),
    [trackFilterEvent]
  );

  return {
    trackFilterEvent,
    trackFilterEventWithThrottle,
  };
};

export const useTrackClearFilter = () => {
  const { categoryId, categoryName } = useContext(
    ProductCollectionTrackingContext
  );
  const clearFilter = useCallback(
    (property: string) => {
      pushEvent('productCollectionFilterClear', {
        property,
        categoryId,
        categoryName,
      });
    },
    [categoryId, categoryName]
  );
  return clearFilter;
};
